import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { faultApi } from '@/api/fault.api';
import { useToasts } from '@/context/ToastContext';
import { ApiError } from '@/lib/errors';
import type { FaultTeamKey } from '@/types/api';

const TEAMS: { key: FaultTeamKey; label: string }[] = [
  { key: 'sales', label: 'Sales' },
  { key: 'designers', label: 'Designers' },
  { key: 'shipment', label: 'Shipment' },
  { key: 'vendors', label: 'Vendors' }
];

/**
 * V4.9.19 — Admin editor for the manual-fault rosters (the people selectable
 * in the ticket Fault panel). Add / hide / remove per team. Hiding keeps past
 * fault marks intact; removing deletes the roster row (marks keep their name
 * snapshot). Opened from the Users page (admin-only).
 */
export function FaultRosterModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { push } = useToasts();
  const queryClient = useQueryClient();
  const [activeTeam, setActiveTeam] = useState<FaultTeamKey>('sales');
  const [newName, setNewName] = useState('');

  const rosterQuery = useQuery({
    queryKey: ['fault-roster', 'all'],
    queryFn: () => faultApi.roster(true),
    enabled: open
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['fault-roster'] });
  };
  const err = (e: unknown) =>
    e instanceof ApiError ? e.problem?.detail ?? e.message : undefined;

  const addMut = useMutation({
    mutationFn: (name: string) => faultApi.addMember(activeTeam, name),
    onSuccess: () => {
      setNewName('');
      invalidate();
      push({ kind: 'success', title: 'Added' });
    },
    onError: (e: unknown) => push({ kind: 'error', title: 'Add failed', message: err(e) })
  });
  const toggleMut = useMutation({
    mutationFn: (v: { id: string; active: boolean }) => faultApi.updateMember(v.id, { active: v.active }),
    onSuccess: invalidate,
    onError: (e: unknown) => push({ kind: 'error', title: 'Update failed', message: err(e) })
  });
  const delMut = useMutation({
    mutationFn: (id: string) => faultApi.deleteMember(id),
    onSuccess: () => {
      invalidate();
      push({ kind: 'success', title: 'Removed' });
    },
    onError: (e: unknown) => push({ kind: 'error', title: 'Remove failed', message: err(e) })
  });

  const roster = rosterQuery.data;
  const members = roster?.[activeTeam] ?? [];

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Fault roster"
      footer={
        <button type="button" className="btn btn-primary" onClick={onClose}>
          Done
        </button>
      }
    >
      <p className="text-small text-muted" style={{ marginTop: 0 }}>
        People selectable in a ticket’s Fault panel. Hidden people stay off the picker but remain on past
        tickets; removing deletes them from the roster (past marks keep the name).
      </p>
      <div className="flex gap-2" style={{ marginBottom: 12, flexWrap: 'wrap' }}>
        {TEAMS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`btn btn-sm ${activeTeam === t.key ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTeam(t.key)}
          >
            {t.label} ({(roster?.[t.key] ?? []).length})
          </button>
        ))}
      </div>
      <form
        className="flex gap-2"
        style={{ marginBottom: 12 }}
        onSubmit={(e) => {
          e.preventDefault();
          const n = newName.trim();
          if (n) addMut.mutate(n);
        }}
      >
        <input
          className="form-input"
          placeholder={`Add to ${TEAMS.find((t) => t.key === activeTeam)?.label}…`}
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <button type="submit" className="btn btn-primary" disabled={addMut.isPending || !newName.trim()}>
          Add
        </button>
      </form>
      {rosterQuery.isLoading ? (
        <div className="text-muted">Loading…</div>
      ) : (
        <div className="table-wrapper" style={{ maxHeight: 360, overflow: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {members.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-muted">No one in this roster yet.</td>
                </tr>
              ) : (
                members.map((m) => (
                  <tr key={m.id} className="no-hover">
                    <td style={{ opacity: m.active ? 1 : 0.5 }}>{m.name}</td>
                    <td>{m.active ? 'Active' : 'Hidden'}</td>
                    <td>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          disabled={toggleMut.isPending}
                          onClick={() => toggleMut.mutate({ id: m.id, active: !m.active })}
                        >
                          {m.active ? 'Hide' : 'Restore'}
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          disabled={delMut.isPending}
                          onClick={() => {
                            if (window.confirm(`Remove ${m.name} from the roster?`)) delMut.mutate(m.id);
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
